import { useEffect, useRef, useState } from 'react'
import { useParams, useSearch, useNavigate, Link } from '@tanstack/react-router'
import { useUser } from '@/hooks/useUsers'
import { usePlans } from '@/hooks/usePlans'
import {
  useClientAlerts,
  useCreateClientAlert,
  useUpdateClientAlert,
  useDeleteClientAlert,
} from '@/hooks/useClientAlerts'
import {
  useClientReports,
  useCreateClientReport,
  useUpdateClientReport,
  useDeleteClientReport,
  useToggleClientReport,
} from '@/hooks/useClientReports'
import {
  useConfigRequest,
  useConfigRequests,
  useUpdateConfigRequest,
} from '@/hooks/useConfigRequests'
import { DataTable, type ColumnDef } from '@/components/common/DataTable'
import { StatusBadge } from '@/components/common/StatusBadge'
import { ConfirmDialog } from '@/components/admin/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate, getApiErrorMessage } from '@/lib/utils'
import { CONFIG_REQUEST_TARGET_LABELS } from '@/lib/constants'
import { toast } from 'sonner'
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  CalendarClock,
  Inbox,
  Pause,
  Pencil,
  Play,
  Trash2,
} from 'lucide-react'
import {
  ClientAlertMetric,
  ClientAlertOperator,
  ClientAlertType,
  ClientReportType,
  ClientScheduleType,
  ConfigRequest,
  CreateUserAlertPayload,
  CreateUserScheduledReportPayload,
  UpdateUserAlertPayload,
  UpdateUserScheduledReportPayload,
  UserAlert,
  UserScheduledReport,
} from '@/types'

// Full label map (10 metrics: 6 active + 4 retired, card 107) — used for ANY
// display of an already-existing alert (table, prefill), so a grandfathered
// alert on a retired metric (e.g. crisis_score) still shows a proper label
// instead of blanking out.
const METRIC_LABELS: Record<ClientAlertMetric, string> = {
  crisis_score: 'Crisis Score',
  brand_health: 'Brand Health',
  trust_index: 'Trust Index',
  virality_rate: 'Virality Rate',
  acceptance_index: 'Indice de Aceptacion',
  toxicity: 'Toxicidad',
  artificial_mobilization: 'Movilización artificial',
  citizen_urgency: 'Urgencia ciudadana',
  outrage_virality: 'Viralidad de indignación',
  discursive_tension: 'Tensión discursiva',
}

// The 6 metrics CORE-API's creation endpoint still accepts (card 107) — the
// 4 retired ones above are grandfathered for existing alerts only. Only
// these populate the metric Select when creating a new alert.
const ACTIVE_METRICS: ClientAlertMetric[] = [
  'acceptance_index',
  'toxicity',
  'artificial_mobilization',
  'citizen_urgency',
  'outrage_virality',
  'discursive_tension',
]

const SCHEDULE_LABELS: Record<ClientScheduleType, string> = {
  daily: 'Diario',
  weekly: 'Semanal',
  monthly: 'Mensual',
}

const REPORT_TYPE_LABELS: Record<ClientReportType, string> = {
  pdf: 'PDF adjunto',
  ai_summary: 'Resumen IA (email)',
  both: 'Ambos',
}

const DAY_LABELS = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo']
const HOURS = Array.from({ length: 24 }, (_, i) => i)
const MINUTES = [0, 15, 30, 45]

/** Ecuador (UTC-5, sin horario de verano) <-> UTC. Mismo criterio que sl-react-core. */
const ecuToUtc = (hourEcu: number) => (hourEcu + 5) % 24
const utcToEcu = (hourUtc: number) => (hourUtc + 19) % 24

const DEFAULT_ALERT_FORM = {
  type: 'metric' as ClientAlertType,
  keyword_id: '',
  // Retired metrics are grandfathered but rejected on create (card 107) —
  // default to an active one so the Select (options = ACTIVE_METRICS) opens
  // on a valid value.
  metric: 'acceptance_index' as ClientAlertMetric,
  operator: 'gt' as ClientAlertOperator,
  threshold_value: 70,
  keywordsInput: '',
}

const DEFAULT_REPORT_FORM = {
  dashboard_name: '',
  schedule_type: 'weekly' as ClientScheduleType,
  day_of_week: 0,
  day_of_month: 1,
  report_type: 'ai_summary' as ClientReportType,
  hour_ecu: 8,
  minute_utc: 0,
  whatsapp_channel: false,
}

const parseKeywords = (input: string): string[] =>
  input
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean)

export const UserConfigPage = () => {
  const { userId } = useParams({ strict: false }) as { userId: string }
  const { fromRequest } = useSearch({ strict: false }) as { fromRequest?: string }
  const navigate = useNavigate()

  const { data: user, isLoading: userLoading } = useUser(userId)
  const { data: plans } = usePlans()
  const { data: alertsData } = useClientAlerts(userId)
  const { data: reports } = useClientReports(userId)

  // Card 95 fix: single source of truth for "can this user receive WhatsApp
  // deliveries right now" — reused by the checkbox render, both payload
  // builders, and the Aplicar prefill so none of them can drift out of sync.
  const whatsappReady = Boolean(user?.whatsapp_number && user?.whatsapp_opt_in_at)

  const createAlert = useCreateClientAlert(userId)
  const updateAlert = useUpdateClientAlert(userId)
  const deleteAlert = useDeleteClientAlert(userId)

  const createReport = useCreateClientReport(userId)
  const updateReport = useUpdateClientReport(userId)
  const deleteReport = useDeleteClientReport(userId)
  const toggleReport = useToggleClientReport(userId)

  const updateConfigRequest = useUpdateConfigRequest()

  // Card 108: GET /admin/config-requests now filters by user_id server-side.
  const [requestsPage, setRequestsPage] = useState(1)
  const { data: requestsData, isLoading: requestsLoading } = useConfigRequests({
    user_id: userId,
    page: requestsPage,
  })
  // Server-side totals; the paginated items are only the current page.
  const { data: pendingRequestsData } = useConfigRequests({
    user_id: userId,
    status: 'pending',
    limit: 1,
  })
  const userRequests = requestsData?.items || []

  const [activeTab, setActiveTab] = useState<'alertas' | 'reportes' | 'solicitudes'>('alertas')

  const [sourceRequestId, setSourceRequestId] = useState<string | undefined>(fromRequest)
  const { data: sourceRequest } = useConfigRequest(sourceRequestId || '')
  const lastPrefilledIdRef = useRef<string | undefined>(undefined)

  // Alert dialogs
  const [alertCreateOpen, setAlertCreateOpen] = useState(false)
  const [alertEditing, setAlertEditing] = useState<UserAlert | null>(null)
  const [deletingAlert, setDeletingAlert] = useState<UserAlert | null>(null)
  const [alertForm, setAlertForm] = useState({ ...DEFAULT_ALERT_FORM })
  const [alertEditForm, setAlertEditForm] = useState({
    is_active: true,
    threshold_value: 70,
    keywordsInput: '',
  })
  const [alertOverride, setAlertOverride] = useState(false)

  // Report dialogs
  const [reportCreateOpen, setReportCreateOpen] = useState(false)
  const [reportEditing, setReportEditing] = useState<UserScheduledReport | null>(null)
  const [deletingReport, setDeletingReport] = useState<UserScheduledReport | null>(null)
  const [reportForm, setReportForm] = useState({ ...DEFAULT_REPORT_FORM })
  const [reportOverride, setReportOverride] = useState(false)

  // "Aplicar" deep-link (inbox or the Solicitudes tab) — pre-fill the create
  // form from the request's payload, then mark it `applied` once submitted.
  useEffect(() => {
    if (!sourceRequest || lastPrefilledIdRef.current === sourceRequest.id) return
    // Wait for the user doc to finish loading before computing whatsappReady
    // below — otherwise it reads as false while useUser is still in flight,
    // the channel gets clamped off, and lastPrefilledIdRef then blocks any
    // later correction once the real (ready) user data arrives.
    if (userLoading || !user) return
    lastPrefilledIdRef.current = sourceRequest.id

    if (sourceRequest.target === 'alerts') {
      const p = sourceRequest.payload as Partial<CreateUserAlertPayload>
      const requestedMetric = (p as { metric?: ClientAlertMetric }).metric
      // A request created before card 107 may carry a now-retired metric.
      // The creation endpoint rejects retired metrics with 400, so never
      // prefill one — clamp to the active default and let the staff know
      // why the value doesn't match what the request originally asked for.
      const isRetiredMetric = !!requestedMetric && !ACTIVE_METRICS.includes(requestedMetric)
      if (isRetiredMetric) {
        toast.info('La métrica solicitada fue retirada; elige una vigente.')
      }
      setAlertForm({
        type: p.type === 'keyword' ? 'keyword' : 'metric',
        keyword_id: (p as { keyword_id?: string }).keyword_id || '',
        metric: !isRetiredMetric && requestedMetric ? requestedMetric : 'acceptance_index',
        operator: (p as { operator?: ClientAlertOperator }).operator || 'gt',
        threshold_value: (p as { threshold_value?: number }).threshold_value ?? 70,
        keywordsInput: ((p as { keywords?: string[] }).keywords || []).join(', '),
      })
      setActiveTab('alertas')
      setAlertCreateOpen(true)
    } else if (sourceRequest.target === 'scheduled_reports') {
      const p = sourceRequest.payload as Partial<CreateUserScheduledReportPayload>
      setReportForm({
        dashboard_name: p.dashboard_name || '',
        schedule_type: p.schedule_type || 'weekly',
        day_of_week: p.day_of_week ?? 0,
        day_of_month: p.day_of_month ?? 1,
        report_type: p.report_type || 'ai_summary',
        hour_ecu: p.hour_utc !== undefined ? utcToEcu(p.hour_utc) : 8,
        minute_utc: p.minute_utc ?? 0,
        // Clamp to whatsappReady — otherwise a prefilled create dialog for a
        // non-ready user would open with the checkbox checked+disabled and
        // no way to uncheck it.
        whatsapp_channel: (p.delivery_channels?.includes('whatsapp') ?? false) && whatsappReady,
      })
      setActiveTab('reportes')
      setReportCreateOpen(true)
    }
  }, [sourceRequest, whatsappReady, userLoading, user])

  // Drop the deep-link state so a later manual create can't mark the
  // original request as applied (review R3-stale-source-request).
  const clearSourceRequest = () => {
    setSourceRequestId(undefined)
    lastPrefilledIdRef.current = undefined
    navigate({ to: '/client-config/$userId', params: { userId }, search: {} })
  }

  const markSourceRequestApplied = async () => {
    if (!sourceRequestId) return
    try {
      await updateConfigRequest.mutateAsync({ id: sourceRequestId, payload: { status: 'applied' } })
    } catch {
      // Entity already exists at this point; only the status PATCH failed. Don't
      // reopen the create dialog (would invite a duplicate) — staff can mark it
      // applied manually from the inbox detail dialog.
      toast.error(
        'Se creo el registro, pero no se pudo marcar la solicitud como aplicada. Marcala manualmente desde el inbox de solicitudes.'
      )
    }
    clearSourceRequest()
  }

  // ------------------------------------------------------------------
  // Guardrails (card 93, item 5) — warn, never hard-block: the backend
  // still enforces the default (enforce_plan_limits=true); the override
  // checkbox is the explicit staff opt-out.
  // ------------------------------------------------------------------
  const plan = plans?.find(
    (p) => p.plan_id === user?.subscription_level || p.name === user?.subscription_level
  )
  const alertsCount = alertsData?.alerts.length || 0
  const maxAlerts = plan?.limits.max_alerts
  const alertsOverLimit =
    typeof maxAlerts === 'number' && maxAlerts >= 0 && alertsCount >= maxAlerts
  const alertsFeatureOff = plan ? !plan.features.alerts_email : false

  const reportsCount = reports?.length || 0
  const maxReports = plan?.limits.max_scheduled_reports
  const reportsOverLimit =
    typeof maxReports === 'number' && maxReports >= 0 && reportsCount >= maxReports
  const reportsFeatureOff = plan ? !plan.features.scheduled_reports : false

  // ------------------------------------------------------------------
  // Alert handlers
  // ------------------------------------------------------------------
  const openAlertCreate = () => {
    clearSourceRequest()
    setAlertForm({ ...DEFAULT_ALERT_FORM })
    setAlertOverride(false)
    setAlertCreateOpen(true)
  }

  const openAlertEdit = (alert: UserAlert) => {
    setAlertEditForm({
      is_active: alert.is_active,
      threshold_value: alert.threshold_value ?? 70,
      keywordsInput: (alert.keywords || []).join(', '),
    })
    setAlertEditing(alert)
  }

  const handleCreateAlert = () => {
    const payload: CreateUserAlertPayload =
      alertForm.type === 'metric'
        ? {
            type: 'metric',
            keyword_id: alertForm.keyword_id,
            metric: alertForm.metric,
            operator: alertForm.operator,
            threshold_value: alertForm.threshold_value,
          }
        : {
            type: 'keyword',
            keyword_id: alertForm.keyword_id,
            keywords: parseKeywords(alertForm.keywordsInput),
          }

    createAlert.mutate(
      { payload, enforceLimits: !alertOverride },
      {
        onSuccess: () => {
          setAlertCreateOpen(false)
          if (sourceRequestId && sourceRequest?.target === 'alerts') void markSourceRequestApplied()
        },
        onError: (err) => {
          // Interceptor already toasts; nothing else to do here.
          void err
        },
      }
    )
  }

  const handleUpdateAlert = () => {
    if (!alertEditing) return
    const payload: UpdateUserAlertPayload =
      alertEditing.type === 'keyword'
        ? {
            is_active: alertEditForm.is_active,
            keywords: parseKeywords(alertEditForm.keywordsInput),
          }
        : {
            is_active: alertEditForm.is_active,
            threshold_value: alertEditForm.threshold_value,
          }

    updateAlert.mutate({ id: alertEditing.id, payload }, { onSuccess: () => setAlertEditing(null) })
  }

  // ------------------------------------------------------------------
  // Report handlers
  // ------------------------------------------------------------------
  const openReportCreate = () => {
    clearSourceRequest()
    setReportForm({ ...DEFAULT_REPORT_FORM })
    setReportOverride(false)
    setReportCreateOpen(true)
  }

  const openReportEdit = (report: UserScheduledReport) => {
    setReportForm({
      dashboard_name: report.dashboard_name,
      schedule_type: report.schedule_type,
      day_of_week: report.day_of_week ?? 0,
      day_of_month: report.day_of_month ?? 1,
      report_type: report.report_type,
      hour_ecu: utcToEcu(report.hour_utc),
      minute_utc: report.minute_utc,
      // Deliberately NOT clamped to whatsappReady — this must reflect the
      // report's true stored state so staff sees an honest "still checked"
      // box if opt-in was cleared after this report was created. The
      // checkbox stays uncheckable-off (never locked) and the payload
      // safety net in handleUpdateReport drops the dead channel on save.
      whatsapp_channel: report.delivery_channels?.includes('whatsapp') ?? false,
    })
    setReportEditing(report)
  }

  const handleCreateReport = () => {
    const payload: CreateUserScheduledReportPayload = {
      dashboard_name: reportForm.dashboard_name,
      schedule_type: reportForm.schedule_type,
      report_type: reportForm.report_type,
      hour_utc: ecuToUtc(reportForm.hour_ecu),
      minute_utc: reportForm.minute_utc,
      // Payload safety net: only re-send "whatsapp" when the user is
      // actually ready. A stale checked checkbox for a now-unready user
      // (see openReportEdit) must never re-trigger the backend's 400.
      delivery_channels:
        reportForm.whatsapp_channel && whatsappReady ? ['email', 'whatsapp'] : ['email'],
      ...(reportForm.schedule_type === 'weekly' ? { day_of_week: reportForm.day_of_week } : {}),
      ...(reportForm.schedule_type === 'monthly' ? { day_of_month: reportForm.day_of_month } : {}),
    }

    createReport.mutate(
      { payload, enforceLimits: !reportOverride },
      {
        onSuccess: () => {
          setReportCreateOpen(false)
          if (sourceRequestId && sourceRequest?.target === 'scheduled_reports')
            void markSourceRequestApplied()
        },
        onError: (err) => {
          // Card 95: surface the "no opted-in WhatsApp number" 400 cleanly —
          // the global interceptor's toast can't unpack this error shape.
          toast.error(getApiErrorMessage(err, 'Error al crear el reporte'))
        },
      }
    )
  }

  const handleUpdateReport = () => {
    if (!reportEditing) return
    const payload: UpdateUserScheduledReportPayload = {
      schedule_type: reportForm.schedule_type,
      report_type: reportForm.report_type,
      hour_utc: ecuToUtc(reportForm.hour_ecu),
      minute_utc: reportForm.minute_utc,
      // Payload safety net — see handleCreateReport.
      delivery_channels:
        reportForm.whatsapp_channel && whatsappReady ? ['email', 'whatsapp'] : ['email'],
      ...(reportForm.schedule_type === 'weekly' ? { day_of_week: reportForm.day_of_week } : {}),
      ...(reportForm.schedule_type === 'monthly' ? { day_of_month: reportForm.day_of_month } : {}),
    }

    updateReport.mutate(
      { id: reportEditing.id, payload },
      {
        onSuccess: () => setReportEditing(null),
        onError: (err) => {
          toast.error(getApiErrorMessage(err, 'Error al actualizar el reporte'))
        },
      }
    )
  }

  const applyFromRequest = (req: ConfigRequest) => setSourceRequestId(req.id)

  // ------------------------------------------------------------------
  // Columns
  // ------------------------------------------------------------------
  const alertColumns: ColumnDef<UserAlert>[] = [
    {
      key: 'type',
      header: 'Tipo',
      cell: (row) => (row.type === 'metric' ? 'Metrica' : 'Palabras clave'),
    },
    {
      key: 'keyword_id',
      header: 'Keyword ID',
      cell: (row) => <span className="font-mono text-xs">{row.keyword_id}</span>,
    },
    {
      key: 'condition',
      header: 'Condicion',
      cell: (row) =>
        row.type === 'metric' ? (
          <span>
            {row.metric ? METRIC_LABELS[row.metric] : '-'} {row.operator === 'gt' ? '>' : '<'}{' '}
            {row.threshold_value}
          </span>
        ) : (
          <span className="text-xs">{(row.keywords || []).join(', ') || '-'}</span>
        ),
    },
    {
      key: 'is_active',
      header: 'Estado',
      cell: (row) => <StatusBadge status={row.is_active ? 'active' : 'inactive'} />,
    },
    {
      key: 'created_at',
      header: 'Creada',
      cell: (row) => (row.created_at ? formatDate(row.created_at) : '-'),
    },
    {
      key: 'actions',
      header: 'Acciones',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => openAlertEdit(row)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeletingAlert(row)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ]

  const reportColumns: ColumnDef<UserScheduledReport>[] = [
    { key: 'dashboard_name', header: 'Dashboard', cell: (row) => row.dashboard_name },
    {
      key: 'schedule',
      header: 'Frecuencia',
      cell: (row) => {
        const label = SCHEDULE_LABELS[row.schedule_type]
        if (row.schedule_type === 'weekly' && row.day_of_week != null) {
          return `${label} (${DAY_LABELS[row.day_of_week]})`
        }
        if (row.schedule_type === 'monthly' && row.day_of_month != null) {
          return `${label} (dia ${row.day_of_month})`
        }
        return label
      },
    },
    { key: 'report_type', header: 'Tipo', cell: (row) => REPORT_TYPE_LABELS[row.report_type] },
    {
      key: 'hour',
      header: 'Hora (Ecuador)',
      cell: (row) =>
        `${String(utcToEcu(row.hour_utc)).padStart(2, '0')}:${String(row.minute_utc).padStart(2, '0')}`,
    },
    {
      key: 'is_active',
      header: 'Estado',
      cell: (row) => <StatusBadge status={row.is_active ? 'active' : 'inactive'} />,
    },
    {
      key: 'next_run_at',
      header: 'Proxima ejecucion',
      cell: (row) => (row.next_run_at ? formatDate(row.next_run_at) : '-'),
    },
    {
      key: 'actions',
      header: 'Acciones',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => toggleReport.mutate({ id: row.id, pause: row.is_active })}
            title={row.is_active ? 'Pausar' : 'Reanudar'}
          >
            {row.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => openReportEdit(row)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeletingReport(row)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ]

  const requestColumns: ColumnDef<ConfigRequest>[] = [
    {
      key: 'kind',
      header: 'Tipo',
      cell: (row) => (row.kind === 'initial_setup' ? 'Config. inicial' : 'Cambio'),
    },
    {
      key: 'target',
      header: 'Objetivo',
      cell: (row) => CONFIG_REQUEST_TARGET_LABELS[row.target] || row.target,
    },
    { key: 'status', header: 'Status', cell: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'created_at',
      header: 'Fecha',
      cell: (row) => (row.created_at ? formatDate(row.created_at) : '-'),
    },
    {
      key: 'actions',
      header: 'Acciones',
      cell: (row) =>
        row.status === 'approved' && row.target !== 'search_settings' ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => applyFromRequest(row)}
            className="gap-1 text-xs"
          >
            Aplicar
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Link to="/client-config" className="text-xs text-primary hover:underline">
            Ver en inbox
          </Link>
        ),
    },
  ]

  if (userLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Usuario no encontrado</p>
        <Button variant="outline" asChild>
          <Link to="/client-config">Volver al inbox</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/client-config">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Config. de Clientes
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold">
          {user.name} {user.lastname}
        </h1>
        <p className="text-muted-foreground">{user.email}</p>
        <div className="mt-2 flex gap-2">
          <StatusBadge role={user.role} />
          <StatusBadge status={user.subscription_level || 'free'} className="capitalize" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Bell className="h-3.5 w-3.5" /> Alertas activas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {(alertsData?.alerts || []).filter((a) => a.is_active).length}
            </p>
            <p className="text-xs text-muted-foreground">de {alertsCount} totales</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <CalendarClock className="h-3.5 w-3.5" /> Reportes activos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {(reports || []).filter((r) => r.is_active).length}
            </p>
            <p className="text-xs text-muted-foreground">de {reportsCount} totales</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Inbox className="h-3.5 w-3.5" /> Solicitudes pendientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{pendingRequestsData?.total ?? 0}</p>
            <p className="text-xs text-muted-foreground">de {requestsData?.total ?? 0} totales</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="alertas">Alertas</TabsTrigger>
          <TabsTrigger value="reportes">Reportes</TabsTrigger>
          <TabsTrigger value="solicitudes">Solicitudes</TabsTrigger>
        </TabsList>

        <TabsContent value="alertas" className="mt-4 space-y-4">
          {(alertsOverLimit || alertsFeatureOff) && (
            <Alert variant="warning" showIcon>
              <AlertDescription>
                {alertsFeatureOff
                  ? 'El plan de este usuario no incluye alertas de email.'
                  : `Este usuario alcanzo el limite de alertas de su plan (${maxAlerts}).`}{' '}
                El backend igual bloquea la creacion salvo que anules el limite explicitamente.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <Button size="sm" onClick={openAlertCreate}>
              Nueva alerta
            </Button>
          </div>
          <DataTable
            columns={alertColumns}
            data={alertsData?.alerts || []}
            keyExtractor={(a) => a.id}
          />
        </TabsContent>

        <TabsContent value="reportes" className="mt-4 space-y-4">
          {(reportsOverLimit || reportsFeatureOff) && (
            <Alert variant="warning" showIcon>
              <AlertDescription>
                {reportsFeatureOff
                  ? 'El plan de este usuario no incluye reportes programados.'
                  : `Este usuario alcanzo el limite de reportes programados de su plan (${maxReports}).`}{' '}
                El backend igual bloquea la creacion salvo que anules el limite explicitamente.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <Button size="sm" onClick={openReportCreate}>
              Nuevo reporte
            </Button>
          </div>
          <DataTable columns={reportColumns} data={reports || []} keyExtractor={(r) => r.id} />
        </TabsContent>

        <TabsContent value="solicitudes" className="mt-4">
          <DataTable
            columns={requestColumns}
            data={userRequests}
            isLoading={requestsLoading}
            keyExtractor={(r) => r.id}
            emptyMessage="Este usuario no tiene solicitudes de configuracion"
            pagination={{
              page: requestsData?.page || 1,
              pages: requestsData?.pages || 1,
              onPageChange: setRequestsPage,
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Create Alert Dialog */}
      <Dialog
        open={alertCreateOpen}
        onOpenChange={(open) => {
          setAlertCreateOpen(open)
          if (!open) clearSourceRequest()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva alerta</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Tipo de alerta</Label>
              <Select
                value={alertForm.type}
                onValueChange={(v) => setAlertForm((f) => ({ ...f, type: v as ClientAlertType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="metric">Metrica</SelectItem>
                  <SelectItem value="keyword">Palabras clave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Keyword ID</Label>
              <Input
                value={alertForm.keyword_id}
                onChange={(e) => setAlertForm((f) => ({ ...f, keyword_id: e.target.value }))}
                placeholder="ID de la busqueda/keyword del usuario"
              />
            </div>
            {alertForm.type === 'metric' ? (
              <>
                <div className="grid gap-2">
                  <Label>Metrica</Label>
                  <Select
                    value={alertForm.metric}
                    onValueChange={(v) =>
                      setAlertForm((f) => ({ ...f, metric: v as ClientAlertMetric }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Options are ALWAYS the 6 active metrics — a retired
                          metric is never valid on create (card 107), so it
                          must never be offered here. The "Aplicar" prefill
                          above already clamps alertForm.metric to an active
                          value before this Select ever renders. */}
                      {ACTIVE_METRICS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {METRIC_LABELS[value]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Condicion</Label>
                    <Select
                      value={alertForm.operator}
                      onValueChange={(v) =>
                        setAlertForm((f) => ({ ...f, operator: v as ClientAlertOperator }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gt">Supera (&gt;)</SelectItem>
                        <SelectItem value="lt">Cae por debajo de (&lt;)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Umbral (0-100)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={alertForm.threshold_value}
                      onChange={(e) =>
                        setAlertForm((f) => ({ ...f, threshold_value: Number(e.target.value) }))
                      }
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="grid gap-2">
                <Label>Palabras clave (separadas por coma)</Label>
                <Textarea
                  value={alertForm.keywordsInput}
                  onChange={(e) => setAlertForm((f) => ({ ...f, keywordsInput: e.target.value }))}
                  placeholder="crisis, boicot, escandalo..."
                  rows={2}
                />
              </div>
            )}
            {(alertsOverLimit || alertsFeatureOff) && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={alertOverride} onCheckedChange={(v) => setAlertOverride(!!v)} />
                Anular limite del plan (override de staff)
              </label>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAlertCreateOpen(false)
                clearSourceRequest()
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateAlert}
              disabled={createAlert.isPending || !alertForm.keyword_id}
            >
              {createAlert.isPending ? 'Creando...' : 'Crear alerta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Alert Dialog — only the admin-whitelisted fields (card 92) */}
      <Dialog open={!!alertEditing} onOpenChange={(open) => !open && setAlertEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar alerta</DialogTitle>
          </DialogHeader>
          {alertEditing && (
            <div className="grid gap-4 py-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={alertEditForm.is_active}
                  onCheckedChange={(v) => setAlertEditForm((f) => ({ ...f, is_active: !!v }))}
                />
                Activa
              </label>
              {alertEditing.type === 'metric' ? (
                <div className="grid gap-2">
                  <Label>Umbral (0-100)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={alertEditForm.threshold_value}
                    onChange={(e) =>
                      setAlertEditForm((f) => ({ ...f, threshold_value: Number(e.target.value) }))
                    }
                  />
                </div>
              ) : (
                <div className="grid gap-2">
                  <Label>Palabras clave (separadas por coma)</Label>
                  <Textarea
                    value={alertEditForm.keywordsInput}
                    onChange={(e) =>
                      setAlertEditForm((f) => ({ ...f, keywordsInput: e.target.value }))
                    }
                    rows={2}
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertEditing(null)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateAlert} disabled={updateAlert.isPending}>
              {updateAlert.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deletingAlert}
        onOpenChange={(open) => !open && setDeletingAlert(null)}
        title="Eliminar alerta"
        description="Esta accion no se puede deshacer."
        confirmLabel="Eliminar"
        variant="destructive"
        isLoading={deleteAlert.isPending}
        onConfirm={async () => {
          if (deletingAlert) {
            try {
              await deleteAlert.mutateAsync(deletingAlert.id)
              setDeletingAlert(null)
            } catch (err) {
              // Interceptor already toasts the API error message
              void getApiErrorMessage(err)
            }
          }
        }}
      />

      {/* Create Report Dialog */}
      <Dialog
        open={reportCreateOpen}
        onOpenChange={(open) => {
          setReportCreateOpen(open)
          if (!open) clearSourceRequest()
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuevo reporte programado</DialogTitle>
          </DialogHeader>
          <ReportFormFields
            form={reportForm}
            setForm={setReportForm}
            whatsappReady={whatsappReady}
          />
          {(reportsOverLimit || reportsFeatureOff) && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={reportOverride} onCheckedChange={(v) => setReportOverride(!!v)} />
              Anular limite del plan (override de staff)
            </label>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReportCreateOpen(false)
                clearSourceRequest()
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateReport}
              disabled={createReport.isPending || !reportForm.dashboard_name}
            >
              {createReport.isPending ? 'Creando...' : 'Crear reporte'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Report Dialog — dashboard_name is not editable (card 92) */}
      <Dialog open={!!reportEditing} onOpenChange={(open) => !open && setReportEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar reporte: {reportEditing?.dashboard_name}</DialogTitle>
          </DialogHeader>
          <ReportFormFields
            form={reportForm}
            setForm={setReportForm}
            hideDashboardName
            whatsappReady={whatsappReady}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportEditing(null)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateReport} disabled={updateReport.isPending}>
              {updateReport.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deletingReport}
        onOpenChange={(open) => !open && setDeletingReport(null)}
        title="Eliminar reporte programado"
        description="Esta accion no se puede deshacer."
        confirmLabel="Eliminar"
        variant="destructive"
        isLoading={deleteReport.isPending}
        onConfirm={async () => {
          if (deletingReport) {
            await deleteReport.mutateAsync(deletingReport.id)
            setDeletingReport(null)
          }
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared form body for create/edit scheduled report dialogs
// ---------------------------------------------------------------------------

interface ReportFormState {
  dashboard_name: string
  schedule_type: ClientScheduleType
  day_of_week: number
  day_of_month: number
  report_type: ClientReportType
  hour_ecu: number
  minute_utc: number
  whatsapp_channel: boolean
}

function ReportFormFields({
  form,
  setForm,
  hideDashboardName,
  whatsappReady,
}: {
  form: ReportFormState
  setForm: (updater: (f: ReportFormState) => ReportFormState) => void
  hideDashboardName?: boolean
  /** Card 95: user has whatsapp_number + whatsapp_opt_in_at set. */
  whatsappReady: boolean
}) {
  return (
    <div className="grid gap-4 py-2">
      {!hideDashboardName && (
        <div className="grid gap-2">
          <Label>Dashboard (keyword del usuario)</Label>
          <Input
            value={form.dashboard_name}
            onChange={(e) => setForm((f) => ({ ...f, dashboard_name: e.target.value }))}
            maxLength={200}
          />
        </div>
      )}
      <div className="grid gap-2">
        <Label>Frecuencia</Label>
        <Select
          value={form.schedule_type}
          onValueChange={(v) => setForm((f) => ({ ...f, schedule_type: v as ClientScheduleType }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Diario</SelectItem>
            <SelectItem value="weekly">Semanal</SelectItem>
            <SelectItem value="monthly">Mensual</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {form.schedule_type === 'weekly' && (
        <div className="grid gap-2">
          <Label>Dia de la semana</Label>
          <Select
            value={String(form.day_of_week)}
            onValueChange={(v) => setForm((f) => ({ ...f, day_of_week: Number(v) }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAY_LABELS.map((label, i) => (
                <SelectItem key={i} value={String(i)}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {form.schedule_type === 'monthly' && (
        <div className="grid gap-2">
          <Label>Dia del mes</Label>
          <Input
            type="number"
            min={1}
            max={31}
            value={form.day_of_month}
            onChange={(e) => setForm((f) => ({ ...f, day_of_month: Number(e.target.value) }))}
          />
        </div>
      )}
      <div className="grid gap-2">
        <Label>Tipo de reporte</Label>
        <Select
          value={form.report_type}
          onValueChange={(v) => setForm((f) => ({ ...f, report_type: v as ClientReportType }))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ai_summary">Resumen IA (email)</SelectItem>
            <SelectItem value="pdf">PDF adjunto</SelectItem>
            <SelectItem value="both">Ambos</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>Hora (Ecuador)</Label>
          <Select
            value={String(form.hour_ecu)}
            onValueChange={(v) => setForm((f) => ({ ...f, hour_ecu: Number(v) }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {String(h).padStart(2, '0')}:00
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Minutos</Label>
          <Select
            value={String(form.minute_utc)}
            onValueChange={(v) => setForm((f) => ({ ...f, minute_utc: Number(v) }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MINUTES.map((m) => (
                <SelectItem key={m} value={String(m)}>
                  :{String(m).padStart(2, '0')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-1.5">
        <label
          className={`flex items-center gap-2 text-sm ${whatsappReady ? '' : 'text-muted-foreground'}`}
        >
          <Checkbox
            checked={form.whatsapp_channel}
            // Disabled only to block turning it ON for a non-ready user.
            // A report already carrying the channel (edit-seeded from a
            // now-stale delivery_channels) must stay uncheckable so staff
            // can turn it off — a disabled+checked box would be a dead end.
            disabled={!whatsappReady && !form.whatsapp_channel}
            onCheckedChange={(v) => setForm((f) => ({ ...f, whatsapp_channel: !!v }))}
          />
          Enviar también por WhatsApp
        </label>
        {!whatsappReady && (
          <p className="text-xs text-muted-foreground">
            Este usuario no tiene un número de WhatsApp verificado con consentimiento registrado.
            Capturalo desde su ficha de usuario para habilitar este canal.
          </p>
        )}
      </div>
    </div>
  )
}
